import {
  Chip,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import React, { useEffect, useState, useMemo } from "react";
import BlockUi from "react-block-ui";
import { withTranslation } from "react-i18next";
import { BsBookmarkCheckFill, BsBookmarkPlusFill } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import { withRouter } from "react-router-dom/cjs/react-router-dom.min";
import Select from "react-select";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import * as APPCONFIG from "../../config/app-config.js";
import dImage from "../../images/coaching.png";
import defaultAvatar from "../../images/icons/default_bg.png";
import dummyimage from "../../img/home/directory.jpg";
import { getCategoryListV2 } from "../../redux/actions/calendarAction.js";
import { needPhotVerification } from "../../redux/actions/commonApiActions";
import { getAllDirectory, userFavourite } from "../../redux/actions/directoryActions";
import { giveUserAction } from "../../utils";
import { dBody, dropFlowStyle } from "../../components/Utils.js";

// 🔹 Debounce utility
function debounce(func, delay) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => func(...args), delay);
  };
}

function DirectoryList(props) {
  const dispatch = useDispatch();
  const { t } = props;

  const [searchKey, setSearchKey] = useState("");
  const [location, setLocation] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [serviceCategory, setServiceCategory] = useState("");
  const [page, setPage] = useState(0);
  const [clearFilter, setClearFilter] = useState(false);

  const { directoryList, loaderStatus, seeMoreOption } = useSelector(
    (state) => state.directory
  );
  const workspaceType = useSelector(
    (state) => state.loginDetailsApi.loginDetails?.userWorkSpace?.slug
  );
  const loginDetails = useSelector((state) => state.loginDetailsApi.loginDetails);
  const { categoryList } = useSelector((state) => state.event);

  // 🔹 Core search logic
  const runSearch = (params = {}) => {
    const data = {
      searchKey: params.type === "searchKey" ? params.value : searchKey,
      location: params.type === "location" ? params.value : location,
      category: params.type === "serviceCategory" ? params.value?.label ?? "" : serviceCategory,
      owner: params.type === "filterType" ? params.value === "me" : filterType === "me",
      bookmark: params.type === "filterType" ? params.value === "bookmark" : filterType === "bookmark",
    };

    if (clearFilter) {
      data.userId = props?.location?.state?.userId ?? loginDetails.uId;
      data.type = "USER_ID_FILTER";
      data.owner = true;
    }

    console.log("📌 Request Payload =>", data);

    dispatch(getAllDirectory(data, 0, false));
    setPage(1);
  };

  // 🔹 Debounced wrapper for searchKey & location
  const debouncedSearch = useMemo(() => debounce(runSearch, 500), []);

  // 🔹 Input handlers
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchKey(value);
    debouncedSearch({ type: "searchKey", value });
  };

  const handleLocationChange = (e) => {
    const value = e.target.value;
    setLocation(value);
    debouncedSearch({ type: "location", value });
  };

  const handleFilterChange = (e) => {
    const value = e.target.value;
    setFilterType(value);
    runSearch({ type: "filterType", value }); // immediate
  };

  const handleCategoryChange = (val) => {
    setServiceCategory(val || "");
    runSearch({ type: "serviceCategory", value: val || "" }); // immediate
  };

  // 🔹 First load
  useEffect(() => {
    let data = {
      searchKey: "",
      location: "",
      category: "",
      owner: false,
      userId: loginDetails.uId,
      bookmark: false,
    };

    if (props?.location?.state?.type === "USER_ID_FILTER") {
      data.userId = props?.location?.state?.userId ?? loginDetails.uId;
      data.type = "USER_ID_FILTER";
      data.owner = true;
      setClearFilter(true);
    }

    dispatch(getAllDirectory(data, 0, false));
    setPage(1);

    if (categoryList.length === 0) {
      dispatch(getCategoryListV2());
    }
  }, []);

  const clearFilterData = () => {
    const data = {
      searchKey: "",
      location: "",
      category: "",
      owner: false,
      userId: loginDetails.uId,
      bookmark: false,
    };
    setClearFilter(false);
    setSearchKey("");
    setLocation("");
    setServiceCategory("");
    setFilterType("all");
    dispatch(getAllDirectory(data, 0, false));
    setPage(1);
  };

  const seeMoreDirectory = () => {
    const data = {
      searchKey,
      location,
      category: serviceCategory || "",
      owner: filterType === "me",
      bookmark: filterType === "bookmark",
    };
    if (clearFilter) {
      data.userId = props?.location?.state?.userId ?? loginDetails.uId;
      data.type = "USER_ID_FILTER";
      data.owner = true;
    }
    dispatch(getAllDirectory(data, page, true));
    setPage(page + 1);
  };

  const directoryDescription = (directoryId) => {
    props.history.push(`/${workspaceType}/directory-details/${directoryId}`);
  };

  const createNew = () => {
    if (giveUserAction(loginDetails?.profile_complete)) {
      dispatch(needPhotVerification(true));
    } else {
      props.history.push(`/${workspaceType}/business/user-directory/new-directory`);
    }
  };

  const bookMarkStatus = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    Swal.fire({
      text: item.bookmark
        ? "Do you want remove this directory in favourite ⭐ "
        : "Do you want add this directory in favourite ⭐",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      reverseButtons: true,
    }).then((result) => {
      if (result.value) {
        const data = { directoryId: item.directoryId, active: !item.bookmark };
        dispatch(userFavourite(data));
        toast.success(item.bookmark ? "Removed successfully" : "Added successfully");
      }
    });
  };

  return (
    <BlockUi
      tag="div"
      message={t("Loading, please wait")}
      style={{ minHeight: "130px" }}
      className="wallLoadingPage"
      blocking={loaderStatus}
    >
      <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
        <h3 className="listHomeTitle">{t("Business Directory")}</h3>
      </div>

      <div className="spaceFiletCard newPostCardWrapper">
        <Grid container spacing={1}>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <TextField
              style={{ width: "100%", height: "100%" }}
              variant="outlined"
              value={searchKey}
              onChange={handleSearchChange}
              placeholder={t("Search service & product ...")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        <div className="d-flex flex-wrap directory-wrap align-items-center">
          <TextField
            className="text-filed-border"
            style={{ background: "white" }}
            variant="outlined"
            value={location}
            placeholder={t("Search location") + "..."}
            onChange={handleLocationChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <Select
            options={categoryList}
            value={serviceCategory}
            onChange={handleCategoryChange}
            isClearable
            className="z-1000 mw-15"
            placeholder="Filter by category"
            menuPortalTarget={dBody}
            styles={dropFlowStyle}
          />

          {clearFilter ? (
            <Chip
              style={{
                overflow: "hidden",
                fontSize: "12px",
                textOverflow: "ellipsis",
                textTransform: "capitalize",
                maxWidth: "138px",
                border: "1px solid",
                background: "white",
              }}
              label={"Clear Filter"}
              onDelete={clearFilterData}
            />
          ) : (
            <TextField
              className="text-filed-border"
              select
              variant="outlined"
              name="filterType"
              value={filterType}
              onChange={handleFilterChange}
            >
              <MenuItem value="all">{t("Listing")}</MenuItem>
              <MenuItem value="me">{t("Your Listing")}</MenuItem>
              <MenuItem value="bookmark">{t("Favourite")}</MenuItem>
            </TextField>
          )}
        </div>
      </div>

      <div className="row mt-1">
        <div className="col-12 col-md-12 col-lg-4">
          <section
            className="cardWrapper-2 font-bold d-flex align-items-center flex-column"
            style={{ gap: "2rem" }}
          >
            <p className="text-secondary text-center">
              {t("Do you have a business you would like to promote") + "?"}
            </p>
            <button onClick={createNew} className="b-17">
              {t("ENROLL YOUR SERVICE")}
            </button>
            <img src={dImage} style={{ width: "12rem" }} alt="logo" />
          </section>
        </div>

        <div className="col-12 col-md-12 col-lg-8">
          {directoryList &&
            directoryList.map((item) => (
              <section
                key={item?.slug}
                className="d-flex ring justify-content-center cardWrapper-2 mb-4 flex-wrap position-relative"
                style={{ gap: "2rem", cursor: "pointer" }}
                onClick={() => directoryDescription(item?.slug)}
              >
                <div className="bd-container">
                  <img
                    src={`${APPCONFIG.API_URL}auth/image/${item?.serviceImage}`}
                    onError={({ currentTarget }) => {
                      currentTarget.onerror = null;
                      currentTarget.src = dummyimage;
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <section className="d-flex gap-2 flex-wrap">
                    <img
                      style={{ cursor: "pointer", width: "4rem" }}
                      className="rounded-circle"
                      src={
                        APPCONFIG.API_URL +
                        "api/getUserPhoto/" +
                        item.userId +
                        "/" +
                        item.userName
                      }
                      alt="user"
                      onError={({ currentTarget }) => {
                        currentTarget.onerror = null;
                        currentTarget.src = defaultAvatar;
                      }}
                    />
                    <ul className="m-0 p-0" style={{ listStyle: "none" }}>
                      <li className="font-weight-bold text-capitalize mt-1">
                        {item.userName}
                      </li>
                      <li className="text-secondary" style={{ fontSize: "12px" }}>
                        {item.location}
                      </li>
                    </ul>
                  </section>
                  <h5 className="font-weight-bold mt-3 text-secondary">
                    {item.serviceName}
                  </h5>
                  <p
                    className="elpsis-line"
                    dangerouslySetInnerHTML={{ __html: item.serviceDetails }}
                  />
                  {item.serviceCategory && (
                    <p className="mt-3 bg-color" style={{ fontSize: "12px" }}>
                      Category : {item.serviceCategory}
                    </p>
                  )}
                </div>
                {loginDetails.uId !== item.userId && (
                  <>
                    {item.bookmark ? (
                      <BsBookmarkCheckFill
                        className="directory-mark bg-color"
                        onClick={(e) => bookMarkStatus(e, item)}
                      />
                    ) : (
                      <BsBookmarkPlusFill
                        className="directory-mark"
                        onClick={(e) => bookMarkStatus(e, item)}
                      />
                    )}
                  </>
                )}
              </section>
            ))}

          {!loaderStatus &&
            seeMoreOption &&
            directoryList &&
            directoryList.length > 0 && (
              <div
                className="d-flex justify-content-center mt-4"
                style={{ marginBottom: "12rem" }}
              >
                <button onClick={seeMoreDirectory}>{t("See more")}</button>
              </div>
            )}

          {!loaderStatus && directoryList && directoryList.length === 0 && (
            <div className="aboutMyRequestWrapper mt-4">
              <p style={{ textAlign: "center" }}>
                {t("No more directory available")}
              </p>
            </div>
          )}
        </div>
      </div>
    </BlockUi>
  );
}

export default withTranslation()(withRouter(DirectoryList));
